// Image-set switcher — the lineage modal's photo tabs.
// Design: design/Image set switcher.dc.html · calls answered in
// design/IMAGE_SET_SWITCHER_ANSWERS_2026-08-17.md (owner rulings at the bottom of that file).
//
// WHY THIS EXISTS, because it reads like a viewer and is not one. The owner's photo ruling is
// clean > unknown > logo > phone number, and THE TOP TIER IS THE ONE TIER NOTHING CAN DETECT:
// every mark signal we hold is portal-declared (`kind='photo_watermarked'`, capture-time
// `is_watermarked`) and means "the portal stamped its logo". A phone number burned into the photo
// by the OWNER is invisible to all of them — which is the case that was reported (korter
// renditions carrying a phone number while every signal called them clean). This surface is a
// human standing in for a classifier we do not have, so the ⚠️ note below is not decoration:
// it is the one thing the panel must not leave unsaid.
//
// THREE OWNER RULINGS THAT LOOK LIKE STYLING AND ARE NOT:
//  1. Clean and watermarked are SEPARATE TABS, not a toggle — asked for explicitly, after being
//     shown that 46,200 properties (89.8%) will therefore show two tabs holding the same
//     pictures. That two-tab state is the NORMAL state. Do not "simplify" it back to a toggle.
//  2. NO TAB IF NO IMAGES — "not disabled, not show at all". A tab is an ASSERTION that there is
//     something behind it (the rule B577 was filed under the same day). Building images and owner
//     uploads have no store at all, so they render nothing — not a greyed pill.
//     ⚠️ This does NOT reach the single-set case: that tab is disabled because there is nowhere
//     to switch TO, not because it is empty. "No images" means no rows.
//  3. The count is DISTINCT PICTURES, not the sum of set counts. Summing renditions claims 48
//     photos for a flat that holds 24 — a number the click cannot deliver.
import { useEffect, useRef, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { COUNTER } from './primitives.jsx'
import { PLATFORM_LABEL, platformKey } from '../ListingsTab.jsx'
import { getPropertyImageSets } from '../../db/core.js'
import { composeVersionSets } from '../../lib/myVersion.js'

// The three mark tiers the server can report, in the owner's own priority order. `unknown` is
// the FALLBACK for anything unrecognised (including a missing `mark`), because it is the label
// that claims least — calling an unscreened photo "clean" is the exact promise this panel exists
// to avoid making.
const MARK = {
  clean:   { key: 'desk_imgset_mark_clean',   cls: 'bg-emerald-500/90 text-white' },
  logo:    { key: 'desk_imgset_mark_logo',    cls: 'bg-amber-500/90 text-white' },
  unknown: { key: 'desk_imgset_mark_unknown', cls: 'bg-black/65 text-white' },
}
export const markOf = set => MARK[set?.mark] || MARK.unknown

// Rendition glyph. Deliberately a shape, not a word: the tab already carries the platform name
// and a count, and on a 1024px modal a third word per pill is what pushes the bar into a scroll.
const GLYPH = { clean: '○', logo: '◍', thumb: '▫', upload: '▲', picked: '◆' }
// ⚠️ The Lynceus pack (server `lynceus: true`, owner 2026-10-07) is the SYSTEM'S PICK — the public page's gallery —
// not a clean rendition: "Lynceus · clean" was shown over a photo with the owner's phone number on it. It reads
// "picked"; whether its photos are clean is the set's `mark`, which the server derives from what was checked.
export const renditionOf = set =>
  set?.lynceus ? 'picked'
  : set?.source === 'manual' || set?.source === 'sentavus' ? 'upload'
  : set?.kind === 'photo_watermarked' ? 'logo'
  : set?.is_thumb ? 'thumb' : 'clean'

// One row per LISTING (owner call 6: per platform, listings nested) — a flat can carry several
// ads on one portal, and a cluster's listings can sit on several property rows, so the group key
// is the listing, labelled with its platform.
export function groupSets(sets) {
  const order = []
  const by = new Map()
  for (const s of (sets || [])) {
    const k = s.listing_id || s.source || '—'
    if (!by.has(k)) { by.set(k, { key: k, source: s.source, tabs: [] }); order.push(k) }
    by.get(k).tabs.push(s)
  }
  return order.map(k => by.get(k))
}

export function ImageSetTabs({ sets, activeId, onPick }) {
  const groups = groupSets(sets)
  // ⚠️ Hooks before the early return — `sets` can legitimately be empty and a conditional hook
  // would blow up the modal on exactly the properties that have no photos.
  const barRef = useRef(null)
  const [edge, setEdge] = useState({ l: false, r: false })
  const measure = () => {
    const n = barRef.current
    if (!n) return
    const l = n.scrollLeft > 4
    const r = n.scrollLeft + n.clientWidth < n.scrollWidth - 4
    setEdge(p => (p.l === l && p.r === r) ? p : { l, r })
  }
  useEffect(() => {
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sets])
  if (!groups.length) return null
  // Single set: one disabled pill + the statement (owner call 3). It is disabled rather than
  // absent because there IS a set — what is absent is anywhere to switch to.
  const only = sets.length === 1
  const nudge = dir => {
    const n = barRef.current
    if (n) n.scrollBy({ left: dir * n.clientWidth * 0.8, behavior: 'smooth' })
  }
  // ⚠️ THE BAR MUST BE REACHABLE, and hiding the scrollbar is what broke that (owner-reported
  // 2026-08-17: the sixth tab was clipped at the right edge with no way to get to it). It sits
  // over a photo, so a native scrollbar is ugly and a trackpad-only gesture is not a control —
  // hence the same ‹ › + fade treatment the design mock used. `onWheel` maps a VERTICAL wheel
  // onto horizontal scroll, because a mouse over this strip has no horizontal axis to give.
  return (
    <div className="relative">
      {edge.l && <span className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 z-20
        bg-gradient-to-r from-black/90 to-transparent" />}
      {edge.r && <span className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 z-20
        bg-gradient-to-l from-black/90 to-transparent" />}
      {edge.l && (
        <button type="button" title={t('desk_imgset_more_sets')} onClick={e => { e.stopPropagation(); nudge(-1) }}
          className="absolute left-0.5 top-1/2 -translate-y-1/2 z-30 w-6 h-6 rounded-full border border-white/35
                     bg-black/70 text-white text-sm leading-none flex items-center justify-center">‹</button>
      )}
      {edge.r && (
        <button type="button" title={t('desk_imgset_more_sets')} onClick={e => { e.stopPropagation(); nudge(1) }}
          className="absolute right-0.5 top-1/2 -translate-y-1/2 z-30 w-6 h-6 rounded-full border border-white/35
                     bg-black/70 text-white text-sm leading-none flex items-center justify-center">›</button>
      )}
      <div ref={barRef} onScroll={measure}
        onWheel={e => { const n = barRef.current; if (n && e.deltaY && !e.deltaX) n.scrollLeft += e.deltaY }}
        className="flex gap-3 overflow-x-auto pb-1.5 no-scrollbar" style={{ scrollbarWidth: 'none' }}>
      {groups.map(g => (
        <div key={g.key} className="flex-none flex gap-1.5">
          {g.tabs.map(s => {
            const on = s.id === activeId
            const rendition = renditionOf(s)
            return (
              <button key={s.id} type="button" disabled={only}
                onClick={e => { e.stopPropagation(); onPick?.(s.id) }}
                title={tLog('desk_imgset_tab_title', {
                  n: s.count, mark: t(markOf(s).key),
                  platform: PLATFORM_LABEL[platformKey(s.source)] || s.source,
                })}
                className={`box-border inline-flex items-center h-7 px-2.5 gap-1.5 rounded-full text-xs leading-none whitespace-nowrap max-w-full border
                  ${only ? 'cursor-not-allowed border-border/60 bg-muted/40 text-muted-foreground'
                         : on ? 'cursor-pointer border-white bg-white text-black'
                              : 'cursor-pointer border-white/25 bg-black/25 text-white/70 backdrop-blur-[2px] hover:bg-black/60 hover:text-white hover:border-white/45 transition-colors'}`}>
                <span className="w-3.5 inline-flex justify-center flex-none">{GLYPH[rendition]}</span>
                <span className="truncate">{setLabel(s, rendition)}</span>
                <span className={`${COUNTER} tabular-nums ${on ? 'bg-black/10 text-black' : 'bg-white/15 text-white/70'}`}>{s.count}</span>
              </button>
            )
          })}
        </div>
      ))}
      </div>
    </div>
  )
}

// ⚠️ The platform name comes from PLATFORM_LABEL/platformKey, which is what every other pill in
// this modal already renders — a bespoke S/M/K label here would drift from PlatformPill the first
// time a portal is added.
export function setLabel(s, rendition = renditionOf(s)) {
  // A version is named as a version (owner 2026-09-28): two identical "ჩვენ ატვირთული" tabs — the
  // agent's version and an owner's submitted photos — could not be told apart.
  if (s?.is_version) return s.mine ? t('desk_imgset_label_my_version') : t('desk_imgset_label_colleague_version')
  // owner 2026-10-22: "Store it and call it owner sent through wa. Keep it as a group here" - pictures an owner sent on WhatsApp
  if (s?.whatsapp_owner || s?.source === 'wa_owner') return t('desk_imgset_label_whatsapp_owner')
  if (rendition === 'upload') return s.author_name || t('desk_imgset_label_upload')
  const platform = PLATFORM_LABEL[platformKey(s.source)] || s.source
  return `${platform} · ${t(`desk_imgset_rendition_${rendition}`)}`
}

// ⚠️ THERE IS NO PER-SET NOTE, AND ITS ABSENCE IS A DECISION (owner 2026-08-17: "I didn't ask
// for this comments, remove it"). An earlier build printed a sentence describing the active set
// plus a standing caveat that our mark signal is portal-declared and cannot see a phone number
// burned into the photo. The caveat is still TRUE — it is recorded in
// design/IMAGE_SET_SWITCHER_ANSWERS_2026-08-17.md and in this file's header — it is simply not
// printed on the card. Do not reintroduce it without asking.

// Header counter. ⚠️ `distinct_photos` is the SERVER's deduped number and the sum of set counts
// is not a substitute for it — see ruling 3 at the top. When the server does not supply it we
// say nothing rather than print the double-counted sum.
export function ImageSetTotal({ sets, distinctPhotos }) {
  if (!sets?.length) return null
  return (
    <span className="font-mono text-[11px] text-muted-foreground/80">
      {distinctPhotos == null
        ? tLog('desk_imgset_total_sets_only', { sets: sets.length })
        : tLog('desk_imgset_total', { sets: sets.length, photos: distinctPhotos })}
    </span>
  )
}

// Fetches the property's sets. Fails SILENT and EMPTY: the switcher is additive, and a property
// whose sets cannot be loaded must fall back to the gallery the modal already renders rather
// than replace it with an error. The caller keys off `sets.length` for exactly that reason.
// ⚠️ The ACTIVE set lives in here, not in the caller, and that is load-bearing: the caller can
// only reach `merged` inside a render-time IIFE, where a useState would break the rules of hooks.
// Keeping selection here means the caller needs exactly one hook call at its top level.
export function usePropertyImageSets(propertyId) {
  const [state, setState] = useState({ sets: [], distinctPhotos: null, loading: false })
  const [activeId, setActiveId] = useState(null)
  useEffect(() => {
    if (!propertyId) { setState({ sets: [], distinctPhotos: null, loading: false }); return }
    let cancelled = false
    setState(s => ({ ...s, loading: true }))
    getPropertyImageSets(propertyId)
      .then(r => {
        if (cancelled) return
        const sets = composeVersionSets(Array.isArray(r?.sets) ? r.sets.filter(s => s?.id && (s.urls || []).length) : [])
        setState({ sets, distinctPhotos: r?.distinct_photos ?? null, loading: false })
      })
      .catch(() => { if (!cancelled) setState({ sets: [], distinctPhotos: null, loading: false }) })
    return () => { cancelled = true }
  }, [propertyId])
  // Resolved at RENDER, not in an effect that writes state: an effect would leave one frame with
  // no active set, and the hero would flash the pre-switcher gallery every time a property opens.
  // A stale id (the modal re-anchored to another property) falls back to the first set the same
  // way, so `active` is never a set that is not in `sets`.
  const active = state.sets.find(s => s.id === activeId) || state.sets[0] || null
  return { ...state, activeId: active?.id ?? null, setActiveId, active }
}
